import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
  type RefObject,
} from 'react';

import { useTooltipProvider } from './TooltipProvider.js';

export type OpenOptions = {
  instant?: boolean;
};

export type CloseOptions = {
  instant?: boolean;
};

export type TooltipRelationship = 'description' | 'name';

export type TooltipContextType = {
  /**
   * Clears a scheduled close and nothing else. Unlike `open`, it never
   * schedules an open, so entering a surface that has already closed does not
   * bring it back.
   */
  cancelClose: () => void;
  close: (options?: CloseOptions) => void;
  /**
   * The id a surface takes when it has none of its own. Stable for the life of
   * the root, so it survives a surface that mounts and unmounts.
   */
  id: string;
  isOpen: boolean;
  open: (options?: OpenOptions) => void;
  setDescriptionId: (id: string | undefined) => void;
  /**
   * True from a press on the surface until the pointer is released. The
   * trigger's blur does not close while it is set: that press is what moved
   * focus, the pointer is on the surface, and leaving the surface closes it.
   */
  surfacePressedRef: RefObject<boolean>;
  tooltipRef: RefObject<HTMLDivElement | null>;
  triggerRef: RefObject<HTMLElement | null>;
};

export const TooltipContext = createContext<TooltipContextType | undefined>(
  undefined,
);

export type TooltipDescriptionStore = {
  getSnapshot: () => string | undefined;
  subscribe: (listener: () => void) => () => void;
};

type WritableTooltipDescriptionStore = TooltipDescriptionStore & {
  set: (id: string | undefined) => void;
};

/**
 * Holds the id of the surface currently mounted, or `undefined` when none is.
 * The trigger describes itself with this and not with `id`, so a root whose
 * surface is conditional does not point at an element that is not there.
 *
 * A store rather than a context value, and kept out of `TooltipContext`,
 * because the surface writes this and only the trigger reads it — and a
 * `name` trigger does not even read it, since it withholds the id. Were the
 * id itself the context value, a surface registering on mount would re-render
 * every trigger under the root, `name` ones included. That re-render is not
 * cheap: an `asChild` trigger renders through Radix `Slot`, which re-clones
 * the child element with a fresh ref on every pass, so the whole child
 * subtree renders again with it and no `memo` below the trigger can stop it.
 * `IconButton` is exactly that shape, and a table mounts a hundred at a time.
 * The store's identity never changes, so the context never invalidates, and a
 * `name` trigger's snapshot never changes, so it never re-renders.
 */
const createDescriptionStore = (): WritableTooltipDescriptionStore => {
  let descriptionId: string | undefined;
  const listeners = new Set<() => void>();

  return {
    getSnapshot: () => descriptionId,
    set: (id) => {
      if (id === descriptionId) return;
      descriptionId = id;
      listeners.forEach((listener) => listener());
    },
    subscribe: (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
};

export const TooltipDescriptionContext = createContext<
  TooltipDescriptionStore | undefined
>(undefined);

const DEFAULT_OPEN_DELAY = 700;
const DEFAULT_CLOSE_DELAY = 150;

export type TooltipRootProps = {
  children: ReactNode;
  closeDelay?: number;
  onClose?: () => void;
  onOpen?: () => void;
  openDelay?: number;
};

export const TooltipRoot = (props: TooltipRootProps) => {
  const { children, closeDelay = DEFAULT_CLOSE_DELAY, onClose, onOpen } = props;
  const provider = useTooltipProvider();

  const resolvedOpenDelay =
    props.openDelay ?? provider?.delayDuration ?? DEFAULT_OPEN_DELAY;

  const id = useId();
  const tooltipRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLElement>(null);
  const surfacePressedRef = useRef(false);
  const [isOpen, setIsOpen] = useState(false);
  const [descriptionStore] = useState(createDescriptionStore);
  const isOpenRef = useRef(false);
  const openTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const onOpenRef = useRef(onOpen);
  const onCloseRef = useRef(onClose);
  onOpenRef.current = onOpen;
  onCloseRef.current = onClose;

  const openDelayRef = useRef(resolvedOpenDelay);
  const closeDelayRef = useRef(closeDelay);
  openDelayRef.current = resolvedOpenDelay;
  closeDelayRef.current = closeDelay;

  const providerRef = useRef(provider);
  providerRef.current = provider;

  const cancelClose = useCallback(() => {
    if (!closeTimerRef.current) return;
    clearTimeout(closeTimerRef.current);
    closeTimerRef.current = null;
  }, []);

  const open = useCallback(
    (options?: OpenOptions) => {
      cancelClose();

      if (isOpenRef.current) return;

      if (openTimerRef.current) {
        clearTimeout(openTimerRef.current);
        openTimerRef.current = null;
      }

      const shouldSkipDelay =
        options?.instant || (provider?.isWithinSkipDelay() ?? false);
      const delay = shouldSkipDelay ? 0 : openDelayRef.current;

      openTimerRef.current = setTimeout(() => {
        isOpenRef.current = true;
        setIsOpen(true);
        onOpenRef.current?.();
        provider?.notifyOpen();
        openTimerRef.current = null;
      }, delay);
    },
    [cancelClose, provider],
  );

  const close = useCallback(
    (options?: CloseOptions) => {
      if (openTimerRef.current) {
        clearTimeout(openTimerRef.current);
        openTimerRef.current = null;
      }

      if (!isOpenRef.current) return;

      cancelClose();

      if (options?.instant) {
        isOpenRef.current = false;
        setIsOpen(false);
        onCloseRef.current?.();
        provider?.notifyClose();
        return;
      }

      closeTimerRef.current = setTimeout(() => {
        isOpenRef.current = false;
        setIsOpen(false);
        onCloseRef.current?.();
        provider?.notifyClose();
        closeTimerRef.current = null;
      }, closeDelayRef.current);
    },
    [cancelClose, provider],
  );

  useEffect(() => {
    return () => {
      if (openTimerRef.current) clearTimeout(openTimerRef.current);
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);

      // The provider counts open tooltips, and `isOpenRef` is true for exactly
      // those already counted — including one mid close-delay, whose scheduled
      // `notifyClose` has just been cleared. Skipping this leaves the count
      // above zero for the provider's lifetime, so every later tooltip opens
      // with no delay.
      if (isOpenRef.current) {
        isOpenRef.current = false;
        providerRef.current?.notifyClose();
      }
    };
  }, []);

  const contextValue = useMemo(
    () => ({
      cancelClose,
      close,
      id,
      isOpen,
      open,
      setDescriptionId: descriptionStore.set,
      surfacePressedRef,
      tooltipRef,
      triggerRef,
    }),
    [cancelClose, close, descriptionStore, id, isOpen, open],
  );

  return (
    <TooltipContext.Provider value={contextValue}>
      <TooltipDescriptionContext.Provider value={descriptionStore}>
        {children}
      </TooltipDescriptionContext.Provider>
    </TooltipContext.Provider>
  );
};

export const useTooltip = (): TooltipContextType => {
  const context = useContext(TooltipContext);

  if (context === undefined) {
    throw new Error('useTooltip must be used within a TooltipRoot');
  }

  return context;
};

const subscribeToNothing = () => () => {};
const getServerSnapshot = () => undefined;

/**
 * Selects `undefined` for a `name` trigger rather than leaving the caller to
 * discard the id, so that trigger's snapshot never changes and it never
 * re-renders when a surface registers. Outside a root there is no store, and
 * the result is `undefined` for the same reason.
 */
export const useTooltipDescriptionId = (
  relationship: TooltipRelationship,
): string | undefined => {
  const store = useContext(TooltipDescriptionContext);

  const getSnapshot = useCallback(
    () => (relationship === 'description' ? store?.getSnapshot() : undefined),
    [relationship, store],
  );

  return useSyncExternalStore(
    store?.subscribe ?? subscribeToNothing,
    getSnapshot,
    getServerSnapshot,
  );
};
